import { useState } from 'react';
import { usePlatformProviders } from '@/api/endpointsPlatformProviders';
import { useMe } from '@/api/hooks';
import type { PlatformProvider } from '@/api/schemasPlatformProviders';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PlatformProviderCard } from '@/features/platform-providers/PlatformProviderCard';
import { PlatformTokenSheet } from '@/features/platform-providers/PlatformTokenSheet';
import { sourceNotice } from '@/features/platform-providers/platformProvidersModel';
import { useNow } from '@/lib/useNow';

/**
 * Hangar administration → Expo & Bitrise (SPEC decision 19): Malible's own Expo and Bitrise tokens, with which Hangar
 * sets up every client. An operator sets each once; the API checks it with the provider, keeps it in AWS Secrets
 * Manager and never shows it again. Operators only (the API refuses everyone else, 403).
 */
export function PlatformProvidersPage() {
  const me = useMe();
  const operator = me.data?.user?.platformRole === 'operator';
  const page = usePlatformProviders(operator);
  return (
    <div data-testid="platform-providers-page" className="flex min-w-0 flex-col gap-5">
      <header className="enter flex flex-col gap-1 border-b pb-5">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Expo & Bitrise</h1>
        <p className="max-w-[72ch] text-sm text-muted-foreground">
          Malible&apos;s own Expo and Bitrise accounts. Hangar uses them to set up every new client and to clean up after a deleted one. Each token is checked with the provider, kept in AWS Secrets Manager and never shown again.
        </p>
      </header>
      {me.isPending || (operator && page.isPending) ? <ProvidersSkeleton /> : null}
      {me.data && !operator ? <Locked /> : null}
      {page.isError ? <ErrorState error={page.error} onRetry={() => void page.refetch()} title="Couldn't load Malible's provider tokens" /> : null}
      {page.data ? <Providers expo={page.data.expo} bitrise={page.data.bitrise} editable={page.data.editable} notice={sourceNotice(page.data)} /> : null}
    </div>
  );
}

function Providers({ expo, bitrise, editable, notice }: { expo: PlatformProvider; bitrise: PlatformProvider; editable: boolean; notice: string | null }) {
  const [setting, setSetting] = useState<PlatformProvider | null>(null);
  const now = useNow(30_000);
  return (
    <>
      {notice ? (
        <Alert data-testid="platform-providers-readonly" className="enter">
          <AlertTitle className="col-start-1 col-end-3 flex items-center gap-2">
            <Icon name="lock" size={18} />
            Read only here
          </AlertTitle>
          <AlertDescription className="col-start-1 col-end-3">{notice}</AlertDescription>
        </Alert>
      ) : null}
      <div className="grid min-w-0 gap-4 lg:grid-cols-2">
        {[expo, bitrise].map((p) => <PlatformProviderCard key={p.provider} provider={p} editable={editable} now={now} onSet={setSetting} />)}
      </div>
      <PlatformTokenSheet provider={setting} onOpenChange={(open) => { if (!open) setSetting(null); }} />
    </>
  );
}

function ProvidersSkeleton() {
  return (
    <div data-testid="platform-providers-loading" className="grid gap-4 lg:grid-cols-2" aria-busy="true" aria-label="Loading Malible's provider tokens">
      {[0, 1].map((i) => <Skeleton key={i} className="h-72 w-full rounded-xl" />)}
    </div>
  );
}

function Locked() {
  return (
    <Card className="enter">
      <EmptyState testId="platform-providers-locked" icon="lock" title="Only platform operators manage Malible's provider tokens" description="Hangar uses them to set up every client." />
    </Card>
  );
}
